import { useApolloClient } from '@apollo/client';
import styled from '@emotion/styled';
import gql from 'graphql-tag';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useLicenses, useSelector } from '~Hooks';
import useI18n from '~Translations';
import { FloorPlans } from './__queries__';
import { Box } from '~Styles';
import { useTableUniverse } from '~Components/TableUniverse';
import Grid from '~Scenes/Tables/Scenes/Grid';
import Floorplan from '~Scenes/Tables/Scenes/Floorplan';
import { Content } from '~Scenes/Tables';
import { useHistory } from 'react-router';
import useOrderParking from '~Hooks/useOrderParking';

const Wrapper = styled(Box)`
  height: 100%;
`;

const ViewTypesWrapper = styled.div`
  background-color: var(--colorBlueGray1100);
  width: 176px;
  display: flex;
  flex-flow: column nowrap;
`;

const ViewType = styled.div<{
  selected: boolean;
}>`
  height: 64px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.24px;
  color: var(--colorWhite);
  padding: 8px 12px 8px 8px;
  border-left: 4px solid transparent;
  border-left-color: ${(props) => (props.selected ? 'var(--colorUIYellow)' : 'transparent')};
  border-bottom: 1px solid var(--colorBlueGray1200);
  background: ${(props) =>
    props.selected
      ? 'linear-gradient(90deg, rgb(14, 18, 22) 0%, rgb(28, 35, 44) 100%)'
      : 'var(--colorBlueGray1100)'};
`;

interface Props {
  parkedOrderId: string;
  toggleModal: () => void;
}

/**
 * Component displayed inside the modal when moving to a table
 * Show the table map with clickable tables
 */
const MoveToTable: FC<Props> = ({ parkedOrderId, toggleModal }) => {
  const { i18n } = useI18n();
  const history = useHistory();
  const client = useApolloClient();
  const licenses = useLicenses();

  const orderParking = useOrderParking();
  const tableUniverse = useTableUniverse();
  const allCheques = tableUniverse.cheques;

  const venueId = useSelector((state) => state.venueId) as string;

  /* How we display the tables - Either in a grid view (basic rectangular tables or a given table map (floorPlanId)) */
  const [selectedNav, setSelectedNav] = useState<string>('grid');

  const [tableIdToMoveTo, setTableIdToMoveTo] = useState<string | null>(null);

  const tablesQuery = useMemo(() => {
    try {
      return client.readFragment<FloorPlans>({
        id: venueId,
        fragment: gql`
          fragment FloorPlans on Venue {
            id

            floorPlans {
              id
              name

              tables {
                id
                code
                rotation
                posX
                posY
                deleted

                floorPlan {
                  id
                }
              }

              markers {
                id
                posX
                posY
                size
                icon
              }

              structures {
                id
                posX
                posY
                width
                height
                rotation
              }
            }
          }
        `,
      });
    } catch (e) {
      return null;
    }
  }, [venueId]);

  const floorPlans = tablesQuery?.floorPlans || [];

  const allTables =
    floorPlans.flatMap((floorPlan) => floorPlan.tables).filter((t) => !t.deleted) || [];

  const selectedFloorPlanData = floorPlans.find((fp) => fp.id === selectedNav);
  const markers = selectedFloorPlanData?.markers || [];
  const structures = selectedFloorPlanData?.structures || [];

  /* When a table has been selected, wait for 0.1s to show the user that it registered the click then navigate to the table that the cheque moves to*/
  useEffect(() => {
    if (!tableIdToMoveTo) {
      return;
    }

    orderParking.moveToTable(parkedOrderId, tableIdToMoveTo);

    setTimeout(() => {
      /* If the device has TSR then navigate to TSR otherwise just close the modal since they shouldn't be able to access TSR in that case */
      if (licenses.hasTSR) {
        history.push(`/venue/${venueId}/tables?table=${tableIdToMoveTo}`);
      } else {
        toggleModal();
      }
    }, 100);
  }, [tableIdToMoveTo]);

  return (
    <Wrapper display="flex">
      <ViewTypesWrapper>
        <ViewType selected={selectedNav === 'grid'} onTouchStart={() => setSelectedNav('grid')}>
          {i18n('TM.Grid')}
        </ViewType>
        {floorPlans.map((floorPlan) => (
          <ViewType
            key={floorPlan.id}
            selected={selectedNav === floorPlan.id}
            onTouchStart={() => setSelectedNav(floorPlan.id)}>
            {floorPlan.name}
          </ViewType>
        ))}
      </ViewTypesWrapper>

      <Content>
        {selectedNav === 'grid' ? (
          <Grid
            tables={allTables ?? []}
            allCheques={allCheques}
            selectedTable={tableIdToMoveTo ?? undefined}
            showOnlyOpen={false}
            setSelectedTable={(id) => setTableIdToMoveTo(id)}
          />
        ) : (
          selectedNav.length > 0 && (
            <Floorplan
              allTables={allTables ?? []}
              markers={markers}
              structures={structures}
              selectedFloorPlan={selectedNav}
              allCheques={allCheques}
              selectedTable={tableIdToMoveTo ?? undefined}
              setSelectedTable={(id) => setTableIdToMoveTo(id)}
            />
          )
        )}
      </Content>
    </Wrapper>
  );
};

export default MoveToTable;
